import type { Registry as Services } from '@ember/service';
import { getFeaturesForRoute } from 'lumen/utils/constants/navigation';
import type { RouterContract, SearchParams } from 'shared-react';

const ENTITY_ROUTE_BY_PLACEHOLDER: Record<string, string> = {
  destinationId: 'destination',
  hotelId: 'hotel',
  marketId: 'market',
};

function entityIdInView(
  router: Services['router'],
  placeholder: string,
): string | undefined {
  const routeName = ENTITY_ROUTE_BY_PLACEHOLDER[placeholder];
  if (routeName === undefined) {
    return undefined;
  }
  const id = router.currentRoute?.find((info) => info.name === routeName)
    ?.params?.['id'];
  return typeof id === 'string' ? id : undefined;
}

/**
 * Resolves a `$`-placeholder path. Each `$name` segment is replaced by the
 * matching key in `params`, falling back to the `:id` of the Ember route that
 * owns the placeholder (`$marketId` reads the `market` route). Throws when a
 * placeholder is satisfied by neither, so callers never receive a path that
 * still contains a `$` token.
 *
 * @example
 * // current route: market (id 42)
 * resolvePath(router, '/preview/market/$marketId/channel-performance');
 * // => '/preview/market/42/channel-performance'
 *
 * @example
 * // current route: hotel (id 42)
 * resolvePath(router, '/hotel/$hotelId/$section', { section: 'settings' });
 * // => '/hotel/42/settings'
 */
function resolvePath(
  router: Services['router'],
  to: string,
  params?: Record<string, string>,
): string {
  const missing: string[] = [];

  const path = to
    .split('/')
    .map((segment) => {
      if (!segment.startsWith('$')) {
        return segment;
      }
      const key = segment.slice(1);
      const value = params?.[key] ?? entityIdInView(router, key);
      if (value === undefined) {
        missing.push(key);
        return segment;
      }
      return value;
    })
    .join('/');

  if (missing.length > 0) {
    throw new Error(`Missing route params: ${missing.join(',')}`);
  }
  return path;
}

function subscriptionWithRouteFeatures(
  router: Services['router'],
  navigation: Services['navigation'],
  path: string,
): string | undefined {
  if (!navigation.hasUnifiedHotelView) {
    return undefined;
  }
  const routeName = router.recognize(path)?.name;
  const routeFeatures = routeName ? getFeaturesForRoute(routeName) : undefined;
  if (!routeName || !routeFeatures) {
    return undefined;
  }
  return navigation.getSubscriptionId(routeFeatures, routeName);
}

/**
 * Under unified hotel view the header offers every subscription's features,
 * so `$hotelId` is resolved to the subscription that has the
 * target route's features, as the Ember header's NavigationLink does.
 */
function resolveHotelPath(
  router: Services['router'],
  navigation: Services['navigation'],
  to: string,
  params?: Record<string, string>,
): string {
  const path = resolvePath(router, to, params);
  const isHotelRoute = to.split('/').includes('$hotelId');
  if (!isHotelRoute) {
    return path;
  }
  const subscriptionId = subscriptionWithRouteFeatures(
    router,
    navigation,
    path,
  );
  if (!subscriptionId) {
    return path;
  }
  return resolvePath(router, to, { ...params, hotelId: subscriptionId });
}

function appendSearch(path: string, search?: SearchParams): string {
  if (!search) {
    return path;
  }
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        query.append(key, item);
      }
    } else {
      query.append(key, String(value));
    }
  }
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
}

function toRouteNamePrefix(recognizedName: string): string {
  return recognizedName.replace(/\.index$/, '');
}

/**
 * Translates Ember's RouterService into compatibility-router's contract, the
 * layer mounted React packages route through.
 */
export default function createCompatibilityRouter(
  router: Services['router'],
  navigation: Services['navigation'],
): RouterContract {
  return {
    buildHref: ({ to, params, search }): string =>
      appendSearch(resolveHotelPath(router, navigation, to, params), search),
    isActive: ({ to, params, search }): boolean => {
      const target = router.recognize(resolvePath(router, to, params));
      if (!target) {
        return false;
      }
      const routeName = toRouteNamePrefix(target.name);
      return search
        ? router.isActive(routeName, { queryParams: search })
        : router.isActive(routeName);
    },
    navigate: ({ to, params, search, replace }): void => {
      const url = appendSearch(
        resolveHotelPath(router, navigation, to, params),
        search,
      );
      if (replace) {
        router.replaceWith(url);
      } else {
        router.transitionTo(url);
      }
    },
  };
}
